import { Image, Scroll, Skeleton, Stack } from "@smartfaster-ui/components";
import { AnimatePresence, motion } from "motion/react";
import { useInteriorDesigner } from "./context/InteriorDesignerContext";
import { getActiveImageUrl } from "./context/selectors";
import { usePrevious } from "./hooks/usePrevious";
import { useLingeringValue } from "@huub-web/ui";
import { vars } from "@smartfaster-ui/theming";
import { panImageBoxStyle } from "./Canvas.css";

const DURATION_S = 0.6;

type CanvasProps = {
  /**
   * When true the image is shown as a fixed 4:3 box that fills the available
   * height and can be dragged horizontally if it overflows (compact layout).
   * Otherwise it fills the available space (desktop).
   */
  pan?: boolean;
};

export function Canvas({ pan = false }: CanvasProps) {
  const { state } = useInteriorDesigner();
  const imageUrl = getActiveImageUrl({ state });
  const previousImageUrl = usePrevious(imageUrl);

  const showProgress = useLingeringValue(
    state.isNavigating || state.generation.status === "loading",
    (DURATION_S * 1000) / 2,
  );

  if (!imageUrl) {
    return <Skeleton width="fill" height="fill" />;
  }

  const layers = (
    <>
      {previousImageUrl && (
        <Image
          src={previousImageUrl}
          alt=""
          fit="cover"
          width="fill"
          height="fill"
          placeholder={false}
        />
      )}

      <motion.div
        key={imageUrl}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: DURATION_S }}
      >
        <Image
          src={imageUrl}
          alt=""
          fit="cover"
          width="fill"
          height="fill"
          placeholder={false}
        />
      </motion.div>

      <AnimatePresence>
        {showProgress && (
          <motion.div
            transition={{ duration: DURATION_S / 2 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Skeleton
              width="fill"
              height="fill"
              background="errorText"
              style={{
                background: vars.color.foregroundPrimary,
                /* TODO (Oscar): SMA-1032 — Skeleton's background prop is a no-op, variant styles override it */
                filter: "invert()",
              }}
              opacity="20"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (pan) {
    return (
      <Scroll
        orientation="horizontal"
        hideScrollbar
        overlap={false}
        width="fill"
        height="fill"
      >
        <Stack className={panImageBoxStyle}>{layers}</Stack>
      </Scroll>
    );
  }

  return (
    <Stack width="fill" height="fill">
      {layers}
    </Stack>
  );
}
